/**
 * Authentication API module.
 *
 * Covers `authApi` (login, register, logout, token helpers) and
 * `recoveryApi` (password recovery and reset).
 */

import { getMsg } from "../utils/antdStatic";
import { LoginError, clasificarFalloLogin } from "../utils/loginErrors";
import { apiFetch, API_BASE_URL, setCsrfToken, type OpcionesLlamada } from "./apiClient";
import {
  LoginRequest,
  LoginResponse,
  ApiResponse,
  RegisterRequest,
} from "../interfaces/api.interfaces";

// ─── authApi ──────────────────────────────────────────────────────────────────

export const authApi = {
  /**
   * Logs in a user.
   *
   * Round 2026-04-26 fase 6 (P1-B): el backend setea las cookies
   * HttpOnly `authToken` y `csrfToken` en la respuesta — esa es la fuente
   * de verdad de sesión. Después de migrar todos los call sites legacy a
   * `apiFetch` (que usa cookie auth), ya NO persistimos el JWT en
   * localStorage — vivía expuesto a XSS sin necesidad. El `user` cacheado
   * lo guarda `AuthContext.login()` (cosmético, hidratación rápida).
   *
   * `credentials: "include"` es crítico aquí — sin él el browser descarta
   * las cookies del Set-Cookie cross-origin.
   *
   * Round 2026-10-28: ya NO muestra el toast de error ni lanza un `Error` genérico. Lanza un
   * `LoginError` con la causa clasificada (sin red, demasiados intentos, Turnstile vencido,
   * credenciales, 5xx...) y el que llama decide qué mostrar y qué reportar. Antes el mensaje
   * real vivía solo en un toast de 3 s y la pantalla mostraba «Error en el inicio de sesión».
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
      });
    } catch {
      // fetch solo lanza si la petición no llegó: sin red, DNS, o el navegador la bloqueó
      // (CORS / contenido mixto). Nunca incluye ni se reenvía el cuerpo con la clave.
      throw new LoginError(
        clasificarFalloLogin({ networkError: true, online: navigator.onLine }),
      );
    }

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      // Round 2026-07-27 — se quitó el volcado de `debugReason` a consola.
      // El backend ya no lo envía: exponía el estado del anti-bot a
      // cualquiera que golpeara el endpoint sin autenticarse. El motivo del
      // rechazo de Turnstile queda en el log del servidor.
      throw new LoginError(
        clasificarFalloLogin({
          status: response.status,
          body,
          retryAfterHeader: response.headers.get("Retry-After"),
        }),
      );
    }

    let data: LoginResponse;
    try {
      data = await response.json();
    } catch {
      // 200 con un cuerpo que no es JSON: típico de un proxy/portal cautivo que intercepta la
      // respuesta. No es un fallo de credenciales ni de red; se informa como tal.
      throw new LoginError(
        clasificarFalloLogin({ status: response.status, body: null }),
      );
    }

    // Guardar el csrfToken devuelto en el body — round 2026-04-27. La
    // cookie csrfToken queda en el origen del backend y `document.cookie`
    // del frontend NO la ve cuando los orígenes difieren (dev: 3000 vs
    // 3001 — prod: subdominios distintos en Railway). Sin esto, el
    // header `X-CSRF-Token` salía vacío y todas las mutaciones recibían
    // 403 "CSRF token requerido".
    if ((data as unknown as { csrfToken?: string }).csrfToken) {
      setCsrfToken(
        (data as unknown as { csrfToken: string }).csrfToken,
      );
    }

    getMsg()?.success(data.message || "Inicio de sesión exitoso");
    return data;
  },

  /**
   * Registers a new aseadora. Role is forced to `["aseador"]`.
   */
  register: async (
    userData: RegisterRequest,
    opciones: OpcionesLlamada = {},
  ): Promise<ApiResponse<any> | null> => {
    userData.rol = ["aseador"];
    const payload = {
      nombre_usuario: userData.nombre,
      email_usuario: userData.email,
      rut_quien_recibe: userData.rut,
      banco_quien_recibe: userData.banco,
      tipocuenta_quien_recibe: userData.tipocuenta,
      numerocuenta_quien_recibe: userData.numerocuenta,
      telefono_usuario: userData.telefono || "",
      rol: userData.rol,
      sector: userData.sector,
    };

    return apiFetch<ApiResponse<any>>("/auth/register", {
      method: "POST",
      body: JSON.stringify(payload),
      successMsg: "Aseadora registrada correctamente",
      errorMsg: "Error al registrar la aseadora",
      alFallar: opciones.alFallar,
    });
  },

  /**
   * Cierra sesión: limpia el `user` cacheado Y pega al backend para
   * borrar las cookies HttpOnly (que JS no puede tocar). El POST es
   * fire-and-forget — si falla la red, el storage local igual queda
   * limpio. Round 2026-04-26 fase 6 (P1-B): ya no hay token en storage.
   *
   * Note: calling AuthContext.logout() is preferred as it also updates React state.
   */
  logout: () => {
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    // Limpiar csrf en memoria — round 2026-04-27. La cookie csrfToken la
    // borra el backend en la response del POST de abajo; en memoria la
    // dejamos null para evitar que un fetch entre logout y redirect
    // mande un header con un token ya inválido.
    setCsrfToken(null);
    fetch(`${API_BASE_URL}/auth/logout`, {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
    getMsg()?.success("Sesión cerrada correctamente");
  },

  /**
   * Rehidrata la sesión desde la cookie HttpOnly. Usado por AuthContext al
   * montar para soportar nuevas tabs y refresh — el JWT vive solo en
   * memoria (round 2026-04-26 fase 6 P1-B), pero la cookie HttpOnly
   * `authToken` es cross-tab. El backend reemite un JWT fresco en el body
   * para que el frontend lo guarde en memoria (lo necesita el WebSocket).
   *
   * Devuelve `null` si no hay sesión válida (cookie expirada o ausente);
   * el caller debe limpiar el cache local en ese caso. No usa `apiFetch`
   * porque su 401-handler dispara forceLogout y aquí queremos que el
   * caller decida (puede estar en /login y no debe redirigir).
   */
  me: async (): Promise<{
    token: string;
    csrfToken: string;
    user: {
      id: number;
      nombre: string;
      email: string;
      rol: string;
      sector?: string | null;
      avatar?: string | null;
      avatar_url?: string | null;
    };
  } | null> => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/me`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) return null;
      const data = await res.json();
      // Mismo motivo que en login() — guardar csrfToken en memoria del
      // apiClient para mutaciones posteriores. Round 2026-04-27.
      if (data?.csrfToken) setCsrfToken(data.csrfToken);
      return data;
    } catch {
      return null;
    }
  },
};

// ─── recoveryApi ─────────────────────────────────────────────────────────────

export const recoveryApi = {
  /**
   * Sends a password-recovery e-mail to the given address. `turnstileToken`
   * comes from the Turnstile widget rendered in /recuperar; the backend
   * validates it against Cloudflare before searching the email. Round
   * 2026-04-26 fase 3 (P1-G).
   */
  solicitarRecuperacion: (
    email: string,
    turnstileToken: string,
  ): Promise<ApiResponse<any> | null> =>
    apiFetch<ApiResponse<any>>("/auth/recuperar", {
      method: "POST",
      body: JSON.stringify({ email, turnstileToken }),
      successMsg: "Correo de recuperación enviado correctamente",
      errorMsg: "Error al solicitar recuperación de contraseña",
    }),

  /** Resets the password using a one-time recovery token. */
  resetearContrasena: (
    token: string,
    password: string,
  ): Promise<ApiResponse<any> | null> =>
    apiFetch<ApiResponse<any>>(`/auth/resetear/${token}`, {
      method: "POST",
      body: JSON.stringify({ password }),
      successMsg: "Contraseña restablecida correctamente",
      errorMsg: "Error al restablecer la contraseña",
    }),
};
